import dayjs from 'dayjs';
import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiConfigs } from '../../../api/APIConfigs';
import { BibleStudy } from '../../../api/models/BibleStudy';
import { Person } from '../../../api/models/Person';
import { RhEvent } from '../../../api/models/RhEvent';
import { UploadResult } from '../../../api/models/UploadResult';
import { bibleStudySlice } from '../../../model/bibleStudySlice';
import { RootState } from '../../../model/store';
import { routes } from '../../../route-list';
import { extractStartEndDates } from '../../../util/date-utilities';
import { generateFileFromPlaybackId } from '../../../util/mux-utilities';
import { useAsyncRequest } from '../../../util/use-async-request';
import { useRequest } from '../../../util/use-request';
import { validateEventFormState } from '../../../util/validator';
import { RhConfirmModal } from '../../core/RhConfirmModal';
import { RhSelectOption } from '../../core/RhSelect';
import { EventFormScreen } from '../components/EventFormScreen';
import { EventFormState, initialEventFormState } from './EventFormContainer';

export const EditEventFormContainer = () => {
  const [isSaving, setIsSaving] = useState(false);
  // This state holds the original form values. This should only be set once.
  const [alwaysPristineFormState, setAlwaysPristineFormState] = useState(initialEventFormState);
  const [form, setForm] = useState<EventFormState>(initialEventFormState);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isProgramsModalOpen, setIsProgramsModalOpen] = useState(false);
  const [eventLeaders, setEventLeaders] = useState<RhSelectOption[]>([]);
  const [event, setEvent] = useState<RhEvent | null>(null);
  const bibleStudies = useSelector((state: RootState) => state.bibleStudy.list);

  const request = useRequest();
  const { id: eventId } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const history = useHistory();

  /**
   * Get list of bible study programs for the
   * Bible Study Program Modal
   */
  const { loading: isLoadingStudies } = useAsyncRequest(
    useCallback(() => request<BibleStudy[]>(apiConfigs.getBibleStudies({ showOnlyFromCurrentUser: false })), [request]),
    useCallback(
      bibleStudies => {
        dispatch(bibleStudySlice.actions.receivedList({ bibleStudies }));
      },
      [dispatch]
    )
  );

  /**
   * Get list of event leaders for the
   * Event Leaders select
   */
  const { loading: isLoadingEventLeaders } = useAsyncRequest(
    useCallback(() => request<Person[]>(apiConfigs.getEventLeaders()), [request]),
    useCallback(leaders => {
      setEventLeaders(
        leaders.map((person: Person) => ({
          label: _.startCase(person.name),
          value: person.id,
        }))
      );
    }, [])
  );

  /** Load event by ID on mount */
  const { loading: isLoadingEvent } = useAsyncRequest(
    useCallback(() => {
      return request<RhEvent>(apiConfigs.getEvent(eventId));
    }, [eventId, request]),
    useCallback(
      async result => {
        const event: RhEvent = result;

        // if event has passed, dont load
        if (dayjs(event.startTime).isBefore(new Date())) {
          history.push(routes.EVENTS__VIEW(event.id));
          toast.error('Cannot edit event that has already started!');
          return;
        }

        let introVideo: File | undefined = undefined;
        if (event.introMuxId) {
          try {
            introVideo = await generateFileFromPlaybackId(event.introMuxId);
          } catch (e: any) {
            toast.error('Failed to load Introduction Video');
          }
        }

        const formState: EventFormState = {
          title: event.title || '',
          description: event.description,
          leaderId: event.leaderId,
          endTime: new Date(event.endTime),
          scheduledDate: new Date(event.startTime),
          startTime: new Date(event.startTime),
          tags: event.tags,
          program: event.theProgram,
          vimeoEventUrl: event.vimeoEventUrl || '',
          introVideo,
          publishingStatus: event.publishingStatus,
          // this is for form only, no need to populate
          tag: '',
        };

        setEvent(event);
        setAlwaysPristineFormState(formState);
        setForm(formState);
      },
      [history]
    )
  );

  return (
    <EventFormScreen
      form={form}
      bibleStudies={bibleStudies}
      eventLeaders={eventLeaders}
      isLoadingEventLeaders={isLoadingEventLeaders}
      isSaving={isSaving || isLoadingEvent}
      isEditingTitle={isEditingTitle}
      isProgramsModalOpen={isProgramsModalOpen}
      isLoadingPrograms={isLoadingStudies}
      userClickedCancel={() => {
        // Check first if the form is pristine. If pristine, proceed without confirmation.
        if (_.isEqual(form, alwaysPristineFormState)) {
          history.push(routes.EVENTS__VIEW(eventId));
        } else {
          confirmAlert({
            customUI: options => (
              <RhConfirmModal
                description="Are you sure you want to exit without saving?"
                isOpen={true}
                onClose={options.onClose}
                onOk={() => {
                  options.onClose();
                  history.push(routes.EVENTS__VIEW(eventId));
                }}
              />
            ),
          });
        }
      }}
      userClickedSave={async () => {
        if (!event) {
          toast.error('Event is not yet loaded');
          return;
        }
        if (!form.scheduledDate) {
          toast.error('Event cannot be saved without a Scheduled Date');
          return;
        }
        if (!form.startTime || !form.endTime) {
          toast.error('Event cannot be saved without a Start Time and End Time');
          return;
        }

        const { startTime, endTime } = extractStartEndDates({
          scheduledDate: form.scheduledDate,
          startTime: form.startTime,
          endTime: form.endTime,
        });

        const formError = validateEventFormState({ ...form, startTime, endTime });
        if (formError) {
          toast.error(formError);
          return;
        }

        let introVideoMuxId = event.introMuxId;
        if (form.introVideo) {
          try {
            setIsSaving(true);
            const uploaded = await request<UploadResult>(apiConfigs.uploadVideo(form.introVideo));
            introVideoMuxId = uploaded.muxID;
            setIsSaving(false);
          } catch (e: any) {
            setIsSaving(false);
          }
        }

        try {
          const payload: RhEvent = {
            ...event,
            programId: form.program?.id || event.id,
            startTime: startTime.toJSON(),
            endTime: endTime.toJSON(),
            leaderId: form.leaderId,
            title: form.title,
            description: form.description,
            tags: form.tags,
            introMuxId: introVideoMuxId,
            featuredImage: {
              ...event.featuredImage,
              url: form.program?.imageUrl || event.featuredImage.url,
            },
            publishingStatus: form.publishingStatus ?? 'unpublished',
            vimeoEventUrl: form.vimeoEventUrl,
          };
          setIsSaving(true);
          const result = await request<RhEvent>(apiConfigs.putEvent(payload));
          setIsSaving(false);

          if (form.introVideo && !result.introMuxId) {
            toast.info('Event updated but intro video failed to upload.');
            history.push(routes.EVENTS);
          } else {
            toast.success('Event updated!');
            history.push(routes.EVENTS);
          }
        } catch (e: any) {
          toast.error(e.message || 'Failed to update event');
          setIsSaving(false);
        }
      }}
      userClickedEditTitle={() => {
        setIsEditingTitle(!isEditingTitle);
      }}
      userUpdatedForm={setForm}
      userToggledProgramsModal={() => setIsProgramsModalOpen(!isProgramsModalOpen)}
      userSelectedProgram={program => {
        setForm({ ...form, program });
        setIsProgramsModalOpen(false);
      }}
      userPushedToTags={() => {
        // validate tags for duplicates
        if (form.tags.findIndex(tag => form.tag.toLowerCase() === tag.toLowerCase()) !== -1) {
          toast.error(`Duplicate tag "${form.tag}"`);
          return;
        }
        setForm({
          ...form,
          tags: [...form.tags, form.tag],
          tag: '',
        });
      }}
    />
  );
};
